import type {
  ArchivedAssetType,
  ArchivedAssetTypeMatch,
  AssetType,
  AssetTypeCreateInput,
  AssetTypeField,
  AssetTypeFieldInput,
  AssetTypeSummary,
  AssetTypeUpdateInput,
} from "../domain";
import type {
  ArchivedAssetTypeListItemDto,
  ArchivedAssetTypeMatchDto,
  AssetTypeCreateDto,
  AssetTypeDto,
  AssetTypeFieldDto,
  AssetTypeSummaryDto,
  AssetTypeUpdateDto,
} from "./assetTypeDtos";

export function assetTypeFieldFromDto(dto: AssetTypeFieldDto): AssetTypeField {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    type: dto.type,
    isRequired: dto.isRequired,
    isUnique: dto.isUnique,
    sortOrder: dto.sortOrder,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    assetTypeId: dto.assetTypeId,
    createdById: dto.createdById,
  };
}

export function assetTypeFromDto(dto: AssetTypeDto): AssetType {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    remarks: dto.remarks,
    attachmentsAllowed: dto.attachmentsAllowed,
    status: dto.status,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    accountId: dto.accountId,
    account: dto.account,
    createdById: dto.createdById ?? null,
    updatedById: dto.updatedById ?? null,
    fields: dto.fields.map(assetTypeFieldFromDto),
    ...(dto.relatedAssetsCount !== undefined
      ? { relatedAssetsCount: dto.relatedAssetsCount }
      : {}),
  };
}

export function assetTypeSummaryFromDto(
  dto: AssetTypeSummaryDto,
): AssetTypeSummary {
  return {
    total: dto.totalAssetTypes,
    active: dto.totalActiveAssetTypes,
    inactive: dto.totalInactiveAssetTypes,
  };
}

/** Create's own bare, id-less field write -- update's `fields` reuses this exact shape, matched by label instead of id. */
export function assetTypeFieldInputToDto(field: AssetTypeFieldInput) {
  return {
    nameEn: field.nameEn.trim(),
    nameAr: field.nameAr.trim(),
    type: field.type,
    isRequired: field.isRequired,
    isUnique: field.isUnique,
  };
}

export function assetTypeCreateToDto(
  input: AssetTypeCreateInput,
): AssetTypeCreateDto {
  return {
    ...(input.accountId !== undefined ? { accountId: input.accountId } : {}),
    nameEn: input.nameEn.trim(),
    nameAr: input.nameAr.trim(),
    ...(input.remarks && input.remarks.trim().length > 0
      ? { remarks: input.remarks.trim() }
      : {}),
    attachmentsAllowed: input.attachmentsAllowed,
    fields: input.fields.map(assetTypeFieldInputToDto),
  };
}

export function assetTypeUpdateToDto(
  input: AssetTypeUpdateInput,
): AssetTypeUpdateDto {
  return {
    ...(input.nameEn !== undefined ? { nameEn: input.nameEn.trim() } : {}),
    ...(input.nameAr !== undefined ? { nameAr: input.nameAr.trim() } : {}),
    ...(input.remarks !== undefined ? { remarks: input.remarks.trim() } : {}),
    ...(input.status !== undefined ? { status: input.status } : {}),
    ...(input.fields !== undefined
      ? { fields: input.fields.map(assetTypeFieldInputToDto) }
      : {}),
  };
}

export function archivedAssetTypeMatchFromDto(
  dto: ArchivedAssetTypeMatchDto,
): ArchivedAssetTypeMatch {
  return {
    id: dto.id,
    ...(dto.nameEn !== undefined ? { nameEn: dto.nameEn } : {}),
    ...(dto.nameAr !== undefined ? { nameAr: dto.nameAr } : {}),
  };
}

export function archivedAssetTypeFromDto(
  dto: ArchivedAssetTypeListItemDto,
): ArchivedAssetType {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    status: dto.status,
    account: dto.account,
    fieldsCount: dto.fieldsCount,
    archivedAt: dto.archivedAt,
  };
}
